import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react/macro';
import { Input } from '../../ui/input';
import { Label } from '@/components/ui/label';

// Phone number formatting utility
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');

  // Handle +1 prefix
  if (phoneNumber.startsWith('1') && phoneNumber.length > 1) {
    const withoutPrefix = phoneNumber.substring(1);
    if (withoutPrefix.length <= 3) {
      return `+1 (${withoutPrefix}`;
    } else if (withoutPrefix.length <= 6) {
      return `+1 (${withoutPrefix.slice(0, 3)}) ${withoutPrefix.slice(3)}`;
    } else {
      return `+1 (${withoutPrefix.slice(0, 3)}) ${withoutPrefix.slice(3, 6)}-${withoutPrefix.slice(6, 10)}`;
    }
  }

  // Handle regular US phone numbers
  if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

interface IPhoneField {
  name: string;
  control: Control;
  setTouched: (touched: boolean) => void;
  touched: boolean;
  error: any;
  autoFocus?: boolean;
  enterKeyHint?: 'search' | 'enter' | 'done' | 'go' | 'next' | 'previous' | 'send';
  className?: string;
}

export const PhoneField: React.FC<IPhoneField> = ({
  name,
  control,
  setTouched,
  touched,
  error,
  autoFocus,
  enterKeyHint,
  className,
}) => {
  const { t } = useLingui();
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: t`Phone number is required`,
        validate: (value) => {
          if (!value) return t`Phone number is required`;
          const digits = value.replace(/\D/g, '');
          if (digits.length === 10) return undefined;
          if (digits.length === 11 && digits.startsWith('1')) return undefined;
          return t`Please enter a valid US phone number`;
        },
      }}
      render={({ field: { onChange, value } }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = e.target.value;

          // Extract digits for the actual value
          const allDigits = inputValue.replace(/\D/g, '');

          // Limit digits to valid US phone number lengths
          let digits = allDigits;
          if (allDigits.startsWith('1') && allDigits.length > 11) {
            digits = allDigits.slice(0, 11); // Max 11 digits for +1 prefix
          } else if (!allDigits.startsWith('1') && allDigits.length > 10) {
            digits = allDigits.slice(0, 10); // Max 10 digits for regular US
          }
          // Update the form with clean, limited digits
          onChange(digits);
        };

        const handleFocus = () => {
          setTouched(true);
        };

        const handleBlur = () => {
          setTouched(false);
        };

        const displayValue = value ? formatPhoneNumber(value) : '';
        const shouldShowError = !touched && error;

        return (
          <div className={`mb-4 mx-auto w-full gap-2 ${className}`}>
            <Label className="mb-2 whitespace-nowrap justify-self-start">
              <Trans>Phone Number</Trans>
            </Label>
            <div className="flex-1 min-w-0">
              <Input
                id={name}
                aria-label="Phone number"
                type="tel"
                placeholder="(123) 456-7890"
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoFocus={autoFocus}
                enterKeyHint={enterKeyHint}
                className="flex-1"
              />
              {shouldShowError && (
                <p className="text-red-500 text-sm mt-1 text-center">
                  <Trans>Please enter a valid US phone number</Trans>
                </p>
              )}
            </div>
          </div>
        );
      }}
    />
  );
};
